/* FundOurKids legislator grader — pure scoring logic, no DOM.
   Weights and the card scale come from the design prototype; the badge scale
   comes from the client's scorecard workbook (Oct 2026). Changing either
   changes published grades: agree it with the client, then mirror it here and
   in the tests. */
(function (root) {
  'use strict';

  var WEIGHTS = [15, 10, 5, 10, 10, 10, 10, 10, 10, 10];
  var PARTIAL = [7, 5, 3, 5, 5, 5, 5, 5, 5, 5];
  var LABELS = [
    'Education-First Statute',
    'Education Stabilization Fund',
    'Spending Transparency',
    'MFCU Expansion',
    'Real-Time Eligibility',
    'Medicaid→Education Transfer',
    'Tax Gap Study',
    'SMR Legislation',
    'Data Center Reform',
    'Correctional Education'
  ];

  var GREEN = '#2F8F5B', ORANGE = '#E5533D', RED = '#C0392B';

  function scores(sel) {
    return (sel || []).map(function (v, i) {
      if (v === 'yes') return WEIGHTS[i];
      if (v === 'partial') return PARTIAL[i];
      return 0;
    });
  }

  function total(sel) {
    return scores(sel).reduce(function (a, b) { return a + b; }, 0);
  }

  function actionCount(sel) {
    return scores(sel).filter(function (s) { return s > 0; }).length;
  }

  function categories(sel) {
    var s = scores(sel);
    return [
      { label: 'Education Priority', score: s[0] + s[1] + s[2], max: 30 },
      { label: 'Medicaid Efficiency', score: s[3] + s[4] + s[5], max: 30 },
      { label: 'Tax Gap Study', score: s[6], max: 10 },
      { label: 'Energy Revenue', score: s[7] + s[8], max: 20 },
      { label: 'Systems Reform', score: s[9], max: 10 }
    ];
  }

  /* Live badge scale, from the client's scorecard workbook (Oct 2026). */
  function badgeGrade(t) {
    if (t >= 90) return { letter: 'A+', color: GREEN };
    if (t >= 80) return { letter: 'A',  color: GREEN };
    if (t >= 70) return { letter: 'B+', color: GREEN };
    if (t >= 60) return { letter: 'B',  color: GREEN };
    if (t >= 50) return { letter: 'C+', color: ORANGE };
    if (t >= 40) return { letter: 'C',  color: ORANGE };
    if (t >= 30) return { letter: 'D+', color: RED };
    if (t >= 20) return { letter: 'D',  color: RED };
    if (t >= 10) return { letter: 'F+', color: RED };
    return { letter: 'F', color: RED };
  }

  /* Coarse scale with verdict copy for the generated grade card
     (design: _cardGrade). Copy is verbatim from the design — do not
     paraphrase. */
  function cardGrade(t) {
    if (t >= 80) return {
      letter: 'A', color: GREEN,
      verdict: 'Champion for Children',
      detail: 'This legislator has taken meaningful action on the majority of fundOurKids.org priorities. They deserve public recognition — and continued pressure to complete the remaining actions.'
    };
    if (t >= 60) return {
      letter: 'B', color: GREEN,
      verdict: 'Doing Real Work',
      detail: 'Solid progress on multiple education and revenue priorities. Several key actions remain — constituent pressure at town halls can push them over the line.'
    };
    if (t >= 40) return {
      letter: 'C', color: ORANGE,
      verdict: 'Room to Lead',
      detail: 'Some good votes, but major priorities — particularly on tax gap measurement, SMR, and Medicaid accountability — remain unaddressed. This legislator can do more.'
    };
    if (t >= 20) return {
      letter: 'D', color: RED,
      verdict: 'Needs Accountability',
      detail: 'Few meaningful actions taken for children and fiscal responsibility. Constituents should contact this legislator directly and demand action on the 10 priorities.'
    };
    return {
      letter: 'F', color: RED,
      verdict: 'Failing Our Kids',
      detail: 'No meaningful action documented on any of the 10 fundOurKids.org priorities. This legislator is failing children and taxpayers. Vote. Primary. Replace.'
    };
  }

  var SHARE_TAGS = {
    A: 'a champion for children',
    B: 'doing real work for education',
    C: 'has room to improve for kids',
    D: 'needs accountability from constituents',
    F: 'failing our kids'
  };

  /* Port of the design's _shareText. opts = { name, state, sel } — the
     design reads name/st from React state; we take them as arguments
     since this module has no component state. opts.state maps to the
     design's `st`. */
  function shareText(opts) {
    opts = opts || {};
    var sel = opts.sel || [];
    var s = scores(sel);
    var t = total(sel);
    var n = actionCount(sel);
    var name = opts.name || 'My Legislator';
    var st = opts.state || 'My State';
    var letter = cardGrade(t).letter;
    var tag = SHARE_TAGS[letter];
    var missing = LABELS.filter(function (_, i) { return s[i] === 0; })
      .slice(0, 3)
      .join(' · ') || 'none — all ten actions taken';

    return 'I graded my legislator on FundOurKids.org\n\n' +
      name + ' (' + st + ') received a ' + letter + ' — ' + t + '/100 points on 10 child-and-taxpayer priority actions.\n\n' +
      'They are ' + tag + '.\n\n' +
      'Actions taken: ' + n + '/10\n' +
      'Key missing: ' + missing + '\n\n' +
      'Check your legislator’s grade and take action at FundOurKids.org\n' +
      '#FundOurKids #EducationFirst #StatePolicy #' + st.replace(/\s/g, '');
  }

  /* Longer body for the mailto: it goes to the legislator, so it lists all ten
     actions grouped by answer rather than naming three they missed. */
  function emailBody(opts) {
    opts = opts || {};
    var sel = opts.sel || [];
    var t = total(sel);
    var name = opts.name || 'my legislator';
    var st = opts.state || 'my state';
    var letter = cardGrade(t).letter;
    var card = cardGrade(t);

    var done = [], partial = [], not = [];
    for (var i = 0; i < LABELS.length; i++) {
      if (sel[i] === 'yes') done.push(LABELS[i]);
      else if (sel[i] === 'partial') partial.push(LABELS[i]);
      else not.push(LABELS[i]);
    }

    function group(title, list) {
      if (!list.length) return '';
      return title + ' (' + list.length + '):\n  \u2022 ' + list.join('\n  \u2022 ') + '\n\n';
    }

    return 'I used the FundOurKids.org scorecard to grade ' + name + ' (' + st + ').\n\n' +
      'Grade: ' + letter + ' \u2014 ' + t + '/100 points, ' + actionCount(sel) + ' of 10 actions.\n' +
      card.verdict + '.\n\n' +
      'Here is how each of the ten priorities was marked:\n\n' +
      group('Done', done) +
      group('Partial', partial) +
      group('Not done', not) +
      'The scorecard and its sources are at FundOurKids.org.\n';
  }

  root.FOKGrader = {
    WEIGHTS: WEIGHTS,
    PARTIAL: PARTIAL,
    LABELS: LABELS,
    scores: scores,
    total: total,
    actionCount: actionCount,
    categories: categories,
    badgeGrade: badgeGrade,
    cardGrade: cardGrade,
    shareText: shareText,
    emailBody: emailBody
  };
})(typeof window !== 'undefined' ? window : this);
